import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  MessageSquare,
  Plus,
  Archive,
  Clock,
  Search,
  MessageCircle,
} from "lucide-react";
import { useThreads, type ThreadWithMessages } from "@/hooks/useThreads";
import { useAuth } from "@/hooks/useAuth";

interface ThreadListProps {
  onSelectThread: (threadId: string) => void;
  onNewThread: () => void;
  selectedThreadId: string | null;
}

export default function ThreadList({
  onSelectThread,
  onNewThread,
  selectedThreadId,
}: ThreadListProps) {
  const { threads, loading } = useThreads();
  const { user } = useAuth();
  const [showArchive, setShowArchive] = useState(false);
  const [search, setSearch] = useState("");

  const filteredThreads = threads.filter((t) => {
    const matchesStatus = showArchive
      ? t.status === "closed"
      : t.status === "open";
    const matchesSearch =
      !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.content.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const openCount = threads.filter((t) => t.status === "open").length;
  const closedCount = threads.filter((t) => t.status === "closed").length;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "przed chwila";
    if (minutes < 60) return `${minutes} min temu`;
    if (hours < 24) return `${hours} godz. temu`;
    if (days < 7) return `${days} dni temu`;
    return date.toLocaleDateString("pl-PL");
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-4 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Gorny pasek */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2">
          <Button
            variant={!showArchive ? "default" : "outline"}
            size="sm"
            onClick={() => setShowArchive(false)}
          >
            <MessageSquare className="h-4 w-4 mr-1" />
            Otwarte ({openCount})
          </Button>
          <Button
            variant={showArchive ? "default" : "outline"}
            size="sm"
            onClick={() => setShowArchive(true)}
          >
            <Archive className="h-4 w-4 mr-1" />
            Archiwum ({closedCount})
          </Button>
        </div>
        <Button size="sm" onClick={onNewThread}>
          <Plus className="h-4 w-4 mr-1" />
          Nowy pomysl
        </Button>
      </div>

      {/* Szukaj */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Szukaj pomyslow..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Lista watkow */}
      {filteredThreads.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {showArchive
                ? "Brak zamknietych pomyslow"
                : search
                ? "Nie znaleziono pomyslow"
                : "Brak otwartych pomyslow. Dodaj nowy!"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredThreads.map((thread) => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              isSelected={thread.id === selectedThreadId}
              isOwner={thread.user_id === user?.id}
              onClick={() => onSelectThread(thread.id)}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ThreadCard({
  thread,
  isSelected,
  isOwner,
  onClick,
  formatDate,
}: {
  thread: ThreadWithMessages;
  isSelected: boolean;
  isOwner: boolean;
  onClick: () => void;
  formatDate: (d: string) => string;
}) {
  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-md ${
        isSelected
          ? "ring-2 ring-primary shadow-md"
          : "hover:border-primary/30"
      }`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium text-sm truncate">{thread.title}</h3>
              {thread.status === "closed" && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  Zamkniety
                </Badge>
              )}
            </div>
            {thread.content && (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                {thread.content}
              </p>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDate(thread.last_message_at || thread.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {thread.message_count}{" "}
                {thread.message_count === 1
                  ? "odpowiedz"
                  : thread.message_count < 5
                  ? "odpowiedzi"
                  : "odpowiedzi"}
              </span>
              {isOwner && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  Twoj
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
